import { ObjectState } from "@homebound/form-state";
import { useCallback } from "react";
import { AiFieldSources } from "src/components/formFields/ai/AiProposalValue";
import { useAiCreatedValueProposal } from "src/components/formFields/ai/utils/useAiCreatedValueProposal";
import { LocationPathField } from "src/components/locationPathField/LocationPathField";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { TakeoffsStoreState } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { StoreApi } from "zustand";
import {
  ActionRadioField,
  AddEditItemObjectState,
  CommonFields,
  OnTaskSelect,
  TakeoffAiProposals,
  TakeoffTaskBoundSelectField,
  TliData,
} from "./utils";

type LaborFieldsProps = {
  store: StoreApi<TakeoffsStoreState>;
  formState: ObjectState<AddEditItemObjectState>;
  planPackageId: string;
  initialTliData?: TliData;
  readOnly: boolean;
  takeoffRemovalsEnabled: boolean;
  /** Lays the fields out for the V2 side panel */
  inPanel?: boolean;
  aiProposals?: TakeoffAiProposals;
};

/** The labor line fields, shared by the v1 modal and the V2 side panel. */
export function LaborFields(props: LaborFieldsProps) {
  const { store, planPackageId, initialTliData, readOnly, formState, takeoffRemovalsEnabled, inPanel, aiProposals } =
    props;
  const onTaskSelect: OnTaskSelect = useCallback(
    (id: string | undefined) => {
      formState.taskId.set(id);
    },
    [formState],
  );
  // Only the side panel passes this, for an AI-created line, since the AI's updates only propose Qty, UoM and Task.
  // So it's undefined on updates and in the v1 modal
  const locationProposal = useAiCreatedValueProposal(formState.locationId, aiProposals?.location);

  return (
    <>
      <ActionRadioField field={formState.action} readOnly={readOnly} takeoffRemovalsEnabled={takeoffRemovalsEnabled} />
      <TakeoffTaskBoundSelectField
        required
        selectedTask={initialTliData?.task}
        readOnly={readOnly}
        taskId={formState.taskId}
        aiProposal={aiProposals?.task}
        onSelect={onTaskSelect}
      />
      <LocationPathField
        filter={{ rpav: [store.getState().planPackage.version.id] }}
        newLocationPathField={formState.newLocationPath}
        field={formState.locationId}
        proposedValue={locationProposal.proposedValue}
        helperText={locationProposal.sources && <AiFieldSources {...locationProposal.sources} />}
        onSelect={(locationId, location) => {
          formState.location.set(location);
          locationProposal.resolve(locationId);
        }}
        disabled={disableBasedOnPotentialOperation(initialTliData?.canEditLocation)}
      />
      <CommonFields
        planPackageId={planPackageId}
        planPackageVersionId={store.getState().planPackage.version.id}
        readOnly={readOnly}
        formState={formState}
        takeoffRemovalsEnabled={takeoffRemovalsEnabled}
        disabled={{
          options: disableBasedOnPotentialOperation(initialTliData?.canEditOptions),
          quantity: disableBasedOnPotentialOperation(initialTliData?.canEditQuantity),
        }}
        inPanel={inPanel}
        aiProposals={aiProposals}
      />
    </>
  );
}
